import React, { useEffect, useRef, useState } from "react";
import { get, times } from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import Actions from "src/components/actionbar";
import { SyncScrollLayout } from "../syncScrollLayout";
import { Default } from "react-toastify/dist/utils";
import { Account } from "src/types/accounts";
import Button from "src/components/button";
import { useTinyMce } from "src/components/useTinyMce";
import { Banner } from "src/types/common";
import BrowserService from "src/utils/browser.service";
import { BannerView } from "src/components/banner";
import { CollectionBanner } from "src/types/collections";
import { BannerEditor } from "../bannerEditor";

type LibrarySettingsProps = {
  data: Account | {};
  handleChange: (field: string, value: any) => void;
  showEditor: boolean;
  toggleEditor: () => void;
}

const FavIcon = '/images/Paperflite.png'; 
const defaultBackground = '/images/settings/viewerSettings/default_banner.jpg';

export const LibrarySettings = ({
   data,
   handleChange,
   showEditor,
   toggleEditor,
}: LibrarySettingsProps) => {

  const initialDescriptionRef = useRef(get(data, "librarySettings.message") ?? ""); // Initial description

  const {Renderer: DescriptionViewer , TinyMce: DescriptionEditor , output: updatedDescription, reset: resetDescription } = useTinyMce(initialDescriptionRef.current ?? ''); 
  const [openBannerEditor, setOpenBannerEditor] = useState(false);
  
  useEffect(() => { // handle change for description text
       handleChange("librarySettings.message", updatedDescription);
  }, [ updatedDescription ]);


  // configuration
  const viewerConfiguration = [
    {
      component: <ViewerRenderer 
          logo={ get(data, "logo.full", FavIcon)}
          librarySettings = { get(data, "librarySettings",{})}
          description={<DescriptionViewer />}
          showEditBanner={openBannerEditor}
          handleBannerChange={(field, val) =>  handleChange(field, val)}
          handleCloseBanner={() => setOpenBannerEditor(false)}
        />,
    }  
  ];
  
  const editorConfiguration = [
    {
      component: <EditorRenderer 
           description={
              <DescriptionEditor
               plugins = 'code noneditable, variable, widget,  autolink, link, textcolor, lists, paste'
               toolbar = 'bold italic | alignleft aligncenter alignright | numlist bullist | fontselect | fontsizeselect | forecolor backcolor | link'
               quickToolbar={true}
               classnames={styles.tinyMceDescription}
               transparentBackground={true}
              />
            }
            banner={ get(data, "librarySettings.banner",{}) as CollectionBanner}
            handleChangeBanner={() => {
              toggleEditor();
              setTimeout(() => { // open after editor close
                setOpenBannerEditor(true);
              }, 500)
            }}
        />
    }
  ];

   return (
     <div className={styles.container}>
            <Actions.IconButton
                  icon={"paperflite-up-arrow"}
                  className={cx(styles.previewAction, { [styles.hide]: showEditor })}
                  onClick={() => {
                    toggleEditor();
                  }}
            />
                       
            <SyncScrollLayout 
                  viewerRenderer={viewerConfiguration}
                  editorRenderer={editorConfiguration}  
                  showEditor={showEditor}
                  animateEditorScroll={false}
                  animateViewerScroll={false}
                  classnames={styles.syncScrollContainer}
            />
    </div>
    );
}

export const ViewerRenderer = (
  {logo, description, librarySettings, showEditBanner, handleBannerChange, handleCloseBanner}: 
  {logo: string; description: React.ReactNode, librarySettings?: any, showEditBanner: boolean, handleBannerChange: (field: string, value: any) => void, handleCloseBanner: () => void}) => {


  return(
    <div className={styles.rendererContainer}>
       <div className={cx(styles.contentContainer)}>
        <BannerEditor 
            show={showEditBanner} 
            librarySettings={librarySettings} 
            onChange={(val) =>  handleBannerChange("librarySettings.banner", val)} 
            banner={<BannerView banner={librarySettings?.banner} defaultBanner={defaultBackground} />}
            classnames={styles.banner}
            onClose={() => {
             setTimeout(() => { // let's wait for 1s for banner update and close show banner
                handleCloseBanner();
              }, 1000)
            }}
          />
          { !showEditBanner && 
            <div className={styles.content}>
            <img src={logo} alt="Library Logo" />
            <div className={styles.desc}>
                  <div className={styles.restrictEvents}>
                          {description}
                  </div>
            </div>
            </div> }
      </div>       
      <div className={styles.skeletonContainer}>
                { times(10, (i) => (
                  <div key={i}></div>
                ))}
      </div>
    </div>
  )
}

export const EditorRenderer = ({description, banner, handleChangeBanner}: { description: React.ReactNode, banner?: any, handleChangeBanner: () => void}) => {
    return (
    <div className={styles.editorContainer}>
        <div className={styles.header}>
                <h5 className={styles.title}>Library Settings</h5>
                <p className={styles.subText}>Customize how your library looks for external viewers. Library setting is standardised and will reflect the same for all your audience.</p>
        </div>

        <div className={styles.inputContainer}>
            <p className={styles.label}>Description Text</p>
            {description}
        </div>

        <div className={styles.inputContainer}>
            <p className={styles.label}>Edit background</p>
            <div className={styles.bannerContainer} onClick={handleChangeBanner}>
              <BannerView banner={banner} defaultBanner={defaultBackground}  />
               <Actions.IconButton
                  icon={"paperflite-edit"}
                  className={styles.editIcon}
              />
            </div>
        </div>
    </div>
    )
}
